import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useToast } from "../App";
import PipelineNav from "../components/PipelineNav";
import { api } from "../lib/api";
import type { ProjectDetail } from "../lib/types";

/**
 * The landing page for a project: a light summary plus links into each
 * pipeline stage. Uploading and generating live on their own pages
 * (Sources, Generate) - this page doesn't duplicate their state or forms.
 */
export default function ProjectPage() {
  const { projectId = "" } = useParams();
  const { pushError } = useToast();

  const [project, setProject] = useState<ProjectDetail | null>(null);

  const refresh = useCallback(async () => {
    try {
      setProject(await api.getProject(projectId));
    } catch (error) {
      pushError(error);
    }
  }, [projectId, pushError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!project) return <main className="page"><p className="muted">Loading…</p></main>;

  const ready = project.sources.filter((s) => s.status === "ready").length;
  const processing = project.sources.filter((s) => s.status === "processing" || s.status === "pending").length;
  const failed = project.sources.filter((s) => s.status === "failed").length;
  const counts = project.counts;

  return (
    <main className="page">
      <PipelineNav projectId={projectId} projectName={project.name} current="overview" />

      <div className="page-head">
        <div className="grow">
          <h1>{project.name}</h1>
          <p>Upload your material, then generate cards. Nothing is exported until you've reviewed it.</p>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        <Link to={`/projects/${projectId}/sources`} className="panel" style={{ display: "block" }}>
          <h2 style={{ marginBottom: 6 }}>Sources</h2>
          <p className="muted small" style={{ marginBottom: 10 }}>
            {project.sources.length === 0
              ? "No sources uploaded yet."
              : `${ready} ready${processing ? `, ${processing} processing` : ""}${
                  failed ? `, ${failed} failed` : ""
                }.`}
          </p>
          <span className="btn btn-sm">Manage sources →</span>
        </Link>

        <Link to={`/projects/${projectId}/generate`} className="panel" style={{ display: "block" }}>
          <h2 style={{ marginBottom: 6 }}>Generate</h2>
          <p className="muted small" style={{ marginBottom: 10 }}>
            {counts.cards_total > 0
              ? `${counts.cards_total} cards from ${counts.concepts} concepts.`
              : ready > 0
                ? "Ready to generate."
                : "Upload a source first."}
          </p>
          <span className="btn btn-sm">
            {counts.cards_total > 0 ? "Regenerate →" : "Generate cards →"}
          </span>
        </Link>

        <Link to={`/projects/${projectId}/review`} className="panel" style={{ display: "block" }}>
          <h2 style={{ marginBottom: 6 }}>Review</h2>
          <p className="muted small" style={{ marginBottom: 10 }}>
            {counts.cards_total > 0
              ? `${counts.proposed + counts.flagged} to review, ${counts.accepted} accepted.`
              : "Nothing generated yet."}
          </p>
          <span className="btn btn-sm">Review cards →</span>
        </Link>

        <Link to={`/projects/${projectId}/export`} className="panel" style={{ display: "block" }}>
          <h2 style={{ marginBottom: 6 }}>Export</h2>
          <p className="muted small" style={{ marginBottom: 10 }}>
            {counts.accepted > 0
              ? `${counts.accepted} cards ready to export.`
              : "Accept cards in review first."}
          </p>
          <span className="btn btn-sm">Export to Anki →</span>
        </Link>
      </div>
    </main>
  );
}
